<template>
  <div id="account-signup-container" class="container-fluid">
    <div class="row">
      <div class="col-xs-12 col-md-6 text-center">
        <img
          class="welcome-title"
          src="../assets/fd-welcome.png"
          alt="Welcome"
        />
        <div class="emphasis tagline">
          <p>Collect recipes. Share recipes. Make great food.</p>
          <div class="welcome-text">
            <p>
              Food Delights is a free, online recipe book. Once you create an
              account, you can add recipes you have or find online. Then you can
              share them with your family, friends, or everyone!
            </p>
            <p>
              Not ready to sign up? You can view our
              <b-link :to="{ name: 'recipes' }">public recipes</b-link>.
            </p>
          </div>
        </div>
      </div>

      <div id="account-form-col" class="col-xs-12 col-md-6">
        <div class="card card-shadow">
          <div class="card-body">
            <h5 class="card-title">Create Account</h5>
            <p class="card-text">
              Start collecting recipes today! Signing up is completely free.<br /><span
                class="text-muted"
                >Already have an account?
                <b-link :to="{ name: 'login' }">Login</b-link></span
              >
            </p>
            <form id="frmCreate" @submit.prevent="createUser">
              <b-form-group
                label="Name"
                label-for="input-name"
                :invalid-feedback="invalid.nameFeedback"
                :state="invalid.name"
              >
                <b-form-input
                  id="input-name"
                  v-model="user.name"
                  :state="invalid.name"
                  trim
                ></b-form-input>
              </b-form-group>
              <b-form-group
                label="Email"
                label-for="input-email"
                :invalid-feedback="invalid.emailFeedback"
                :state="invalid.email"
              >
                <b-form-input
                  id="input-email"
                  type="email"
                  v-model="user.email"
                  :state="invalid.email"
                  trim
                ></b-form-input>
              </b-form-group>
              <b-form-group
                label="Password"
                label-for="input-password"
                :invalid-feedback="strength.feedback"
                :state="invalid.password"
              >
                <b-form-input
                  id="input-password"
                  type="password"
                  v-model="user.password"
                  :state="invalid.password"
                  trim
                  @input="measurePasswordStrength"
                ></b-form-input>
                <div class="progress password-strength-bar">
                  <div
                    id="password-strength-bar"
                    :class="
                      'progress-bar progress-bar-danger ' + strength.color
                    "
                    role="progressbar"
                    aria-valuenow="0"
                    aria-valuemin="0"
                    aria-valuemax="100"
                    :style="'width: ' + strength.width + '%'"
                  >
                    <span class="sr-only">0</span>
                  </div>
                </div>
              </b-form-group>
              <b-form-group
                label="Confirm Password"
                label-for="input-confirm"
                :invalid-feedback="invalid.confirmFeedback"
                :state="invalid.confirm"
              >
                <b-form-input
                  id="input-confirm"
                  type="password"
                  v-model="user.confirm"
                  :state="invalid.confirm"
                  trim
                ></b-form-input>
              </b-form-group>
              <div class="form-group">
                <b-form-checkbox id="tos" name="tos" v-model="user.terms">
                  I agree to the
                  <b-link :to="{ name: 'terms' }">terms and conditions</b-link>
                  of using this service
                  <b-form-invalid-feedback :state="invalid.terms"
                    >You must accept the terms and
                    conditions.</b-form-invalid-feedback
                  >
                </b-form-checkbox>
                <b-form-checkbox
                  id="chkNews"
                  name="news_enroll"
                  v-model="user.news_enroll"
                >
                  I want to receive news and updates from Food Delights
                </b-form-checkbox>
              </div>
              <b-button type="submit" variant="primary">
                Create Account
              </b-button>
            </form>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapMutations } from "vuex";
import firebase from "firebase";
import FirebaseMixin from "../mixins/Firebase";
import UtilsMixin from "../mixins/Utils";
import Users from "../firebase/users";
//import moment from "moment";
const zxcvbnImport = () => import("zxcvbn");

export default {
  name: "SignUp",
  mixins: [FirebaseMixin, UtilsMixin],
  //components: { "stripe-card-element": StripeCardElement },
  data() {
    return {
      ref: "users",
      user: {
        name: "",
        email: "",
        password: "",
        confirm: "",
        terms: null,
        news_enroll: null
      },
      invalid: {
        name: null,
        email: null,
        password: null,
        confirm: null,
        terms: null
      },
      strength: {
        width: 0,
        color: "",
        feedback: ""
      }
    };
  },
  watch: {
    userUid: {
      immediate: true,
      handler(uid) {
        if (uid) {
          console.log(uid);
        }
      }
    }
  },
  computed: {
    ...mapState(["isAuthenticated", "userProfile"]),
    userUid() {
      return this.userProfile.uid;
    }
  },
  methods: {
    ...mapMutations(["setUser", "setProfile"]),
    measurePasswordStrength() {
      zxcvbnImport().then(module => {
        const zxcvbn = module.default;
        const result = zxcvbn(this.user.password);
        console.log(result);
        switch (result.score) {
          case 1:
            this.strength.width = 25;
            this.strength.color = "bg-danger";
            this.invalid.password = false;
            break;
          case 2:
            this.strength.width = 50;
            this.strength.color = "bg-warning";
            this.invalid.password = true;
            break;
          case 3:
            this.strength.width = 75;
            this.strength.color = "bg-success";
            this.invalid.password = true;
            break;
          case 4:
            this.strength.width = 100;
            this.strength.color = "bg-success";
            this.invalid.password = true;
            break;
          default:
            if (result.score === 0 && this.user.password.length > 0) {
              this.strength.width = 10;
            } else {
              this.strength.width = 0;
            }
            this.strength.color = "bg-danger";
            this.invalid.password = false;
        }
        this.strength.feedback = result.feedback.warning;
      });
    },
    validateForm() {
      // Reset form validations
      for (let field in this.invalid) {
        if (field !== "password") {
          this.invalid[field] = null;
          this.invalid[field + "Feedback"] = "";
        }
      }
      // Validate fields
      let isValid = true;
      if (!this.user.name || this.user.name.length === 0) {
        this.invalid.name = false;
        this.invalid.nameFeedback = "Please enter a valid name.";
        isValid = false;
      } else {
        this.invalid.name = true;
      }
      if (!this.user.email || !this.validateEmail(this.user.email)) {
        this.invalid.email = false;
        this.invalid.emailFeedback = "Please enter a valid email.";
        isValid = false;
      } else {
        this.invalid.email = true;
      }
      if (!this.user.password || this.user.password.length === 0) {
        this.invalid.password = false;
        this.strength.feedback = "Please enter a valid password.";
        isValid = false;
      }
      if (!this.user.confirm || this.user.password !== this.user.confirm) {
        this.invalid.confirm = false;
        this.invalid.confirmFeedback = "Passwords do not match.";
        isValid = false;
      } else {
        this.invalid.confirm = true;
      }
      if (!this.user.terms) {
        this.invalid.terms = false;
        isValid = false;
      } else {
        this.invalid.terms = true;
      }
      return isValid;
    },
    createUser() {
      if (!this.validateForm()) {
        return false;
      }
      firebase
        .auth()
        .createUserWithEmailAndPassword(this.user.email, this.user.password)
        .then(() => {
          const u = new Users();
          const user = {
            name: this.user.name,
            email: this.user.email,
            news_enroll: this.user.news_enroll
          };
          u.provision(user)
            .then(resp => {
              this.setUser({ name: this.user.name, ...resp.data });
              this.navigate("recipes");
              u.onLogin().then(resp => {
                this.setSearchKey(resp.data.searchKey);
              });
            })
            .catch(error => {
              console.log(error);
              window.toastr.error(
                "An error occurred while creating your account. Please try again later."
              );
            });
        })
        .catch(error => {
          console.log(error);
          window.toastr.error(
            "An error occurred while creating your account. Please try again later."
          );
        });
    }
  },
  created() {}
};
</script>

<style scoped>
#account-signup-container {
  margin: -8px;
  margin-top: -10px;
  width: calc(100% + 16px);
  height: calc(100% + 18px);
  padding: 8px !important;
}

.welcome-title {
  margin-top: 65px;
}

.tagline {
  color: darkslategray;
  text-shadow: 0px 0px 20px white;
  font-size: 22px;
  background: rgba(247, 247, 247, 0.75);
  margin: 0 auto;
  max-width: 500px;
  border-radius: 0.25rem;
}

.welcome-text {
  color: #545a5e;
  font-size: 17px;
  text-align: left;
  padding: 0 15px 10px 15px;
}

#account-form-col {
  max-width: 600px;
}

.password-input {
  border-radius: 0.25rem 0.25rem 0 0;
}

.password-strength-bar {
  height: 5px;
  margin-bottom: 0;
  overflow: hidden;
  background-color: #ced4da;
  border-radius: 0 0 0.25rem 0.25rem;
}

#password-feedback {
  height: 24px;
}
</style>
